import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/auth.store';
import { isAccessDeniedError } from '@/utils/errors';
import { permissionsQueryOptions, type Permissions } from '../api/permissions.api';

let isRefreshing = false;

function holdsTheSame(before: Permissions, after: Permissions): boolean {
  return (
    before.isAdmin === after.isAdmin &&
    [...before.permissions].sort().join('|') === [...after.permissions].sort().join('|')
  );
}

export async function refreshPermissionsAfterDenial(
  error: unknown,
  queryClient: QueryClient,
): Promise<void> {
  if (!isAccessDeniedError(error) || isRefreshing) return;

  const { permissions, isAdmin, arePermissionsLoaded, setPermissions } = useAuthStore.getState();
  if (!arePermissionsLoaded) return;

  isRefreshing = true;
  try {
    const current = await queryClient
      .fetchQuery({ ...permissionsQueryOptions(), staleTime: 0 })
      .catch(() => null);
    if (current === null) return;

    setPermissions(current.permissions, current.isAdmin);
    if (holdsTheSame({ permissions, isAdmin }, current)) return;

    toast.info('Your access has changed', {
      description: 'The menu and the buttons now show what you can do.',
    });
  } finally {
    isRefreshing = false;
  }
}
